<template>
  <header class="bg-slate-900 border-b border-slate-800 px-4 py-2.5 flex items-center justify-between shadow-md select-none">
    <!-- Left: Brand & Logo -->
    <div class="flex items-center space-x-3">
      <div class="w-8 h-8 rounded-lg bg-unibo-red flex items-center justify-center font-bold text-white shadow-lg shadow-unibo-red/20">
        ML
      </div>
      <div>
        <div class="flex items-center space-x-2">
          <h1 class="font-bold text-slate-100 text-base tracking-tight">UNIBO ML Exam Practice</h1>
          <span class="text-xs px-2 py-0.5 rounded-full bg-unibo-red/20 text-unibo-red font-medium border border-unibo-red/30">
            Browser Lab
          </span>
        </div>
        <p class="text-xs text-slate-400 font-mono hidden sm:block">Alma Mater Studiorum — Università di Bologna</p>
      </div>
    </div>

    <!-- Center: Runtime Status & Progress -->
    <div class="hidden lg:flex items-center space-x-6">
      <!-- Pyodide Status -->
      <div class="flex items-center space-x-2 bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-700/60 text-xs">
        <span 
          class="w-2.5 h-2.5 rounded-full animate-pulse"
          :class="{
            'bg-emerald-500 shadow-emerald-500/50 shadow-md': pyodideStatus === 'ready',
            'bg-amber-500 animate-spin': pyodideStatus === 'loading' || pyodideStatus === 'packages_loading',
            'bg-red-500': pyodideStatus === 'error',
            'bg-slate-500': pyodideStatus === 'uninitialized'
          }"
        ></span>
        <span class="text-slate-300 font-medium font-mono">
          Python Pyodide: {{ pyodideStatusMessage }}
        </span>
      </div>

      <!-- Practiced Stats Badge -->
      <div class="flex items-center space-x-2 bg-slate-800/80 px-3 py-1 rounded-full border border-slate-700/60 text-xs">
        <i class="pi pi-check-circle text-emerald-400"></i>
        <span class="text-slate-300">
          Practiced: <strong class="text-emerald-400 font-semibold">{{ doneCount }}/{{ totalCount }}</strong>
        </span>
      </div>
    </div>

    <!-- Right: Actions & Tools -->
    <div class="flex items-center space-x-2">
      <!-- Timer Widget Button -->
      <Button 
        @click="$emit('open-timer')" 
        severity="secondary" 
        size="small" 
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-200"
      >
        <i class="pi pi-clock text-amber-400 mr-1.5"></i>
        <span class="font-mono font-medium text-xs">{{ timerDisplay }}</span>
      </Button>

      <!-- Question Bank Toggle -->
      <Button 
        @click="$emit('toggle-sidebar')" 
        severity="secondary" 
        size="small"
        v-tooltip.bottom="sidebarCollapsed ? 'Show Question Bank (Alt+B)' : 'Hide Question Bank (Alt+B)'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i :class="sidebarCollapsed ? 'pi pi-book text-amber-300' : 'pi pi-book'"></i>
      </Button>

      <!-- Bottom Panel Toggle (Output / Datasets / etc.) -->
      <Button
        @click="$emit('toggle-console')"
        severity="secondary"
        size="small"
        v-tooltip.bottom="consoleCollapsed ? 'Show Output Panel (Alt+O)' : 'Hide Output Panel (Alt+O)'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i :class="consoleCollapsed ? 'pi pi-window-maximize text-amber-300' : 'pi pi-window-minimize'"></i>
      </Button>

      <!-- Onboarding Help -->
      <Button 
        @click="$emit('open-onboarding')" 
        severity="secondary" 
        size="small"
        v-tooltip.bottom="'App Tour & Help'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i class="pi pi-question-circle text-sky-400"></i>
      </Button>

      <!-- Keyboard Shortcuts -->
      <Button 
        @click="$emit('open-shortcuts')" 
        severity="secondary" 
        size="small"
        v-tooltip.bottom="'Keyboard Shortcuts'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i class="pi pi-command"></i>
      </Button>

      <!-- Export / Import Progress -->
      <Button 
        @click="$emit('open-export')" 
        severity="secondary" 
        size="small"
        v-tooltip.bottom="'Backup & Export/Import Data'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i class="pi pi-download text-purple-400"></i>
      </Button>

      <!-- Reset Layout -->
      <Button 
        @click="$emit('reset-layout')" 
        severity="secondary" 
        size="small"
        v-tooltip.bottom="'Reset Window Panels'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i class="pi pi-refresh"></i>
      </Button>

      <!-- Dark / Light Theme Toggle -->
      <Button 
        @click="$emit('toggle-theme')" 
        severity="secondary" 
        size="small"
        v-tooltip.bottom="'Toggle Theme'"
        class="!bg-slate-800 !border-slate-700 hover:!bg-slate-750 !text-slate-300"
      >
        <i :class="theme === 'dark' ? 'pi pi-sun text-amber-300' : 'pi pi-moon text-indigo-400'"></i>
      </Button>
    </div>
  </header>
</template>

<script setup lang="ts">
import Button from 'primevue/button';
import { PyodideStatus } from '../types';

defineProps<{
  pyodideStatus: PyodideStatus;
  pyodideStatusMessage: string;
  doneCount: number;
  totalCount: number;
  timerDisplay: string;
  theme: 'dark' | 'light';
  sidebarCollapsed: boolean;
  consoleCollapsed: boolean;
}>();

defineEmits([
  'open-timer',
  'toggle-sidebar',
  'toggle-console',
  'open-onboarding',
  'open-shortcuts',
  'open-export',
  'reset-layout',
  'toggle-theme'
]);
</script>
